/**
 * Quote and Offer helpers for the procurement-workflow redesign:
 * Supplier_Quote cost entry, the "Ready for Sales Quote" readiness gate, and
 * Quote -> Offer connection / pricing math.
 *
 * Each Quote's `supplierQuotes` map is keyed by supplierId, where every entry
 * is `{ status, lines }` — `status` ('accepted' | 'rejected' | null) lives at
 * the supplier level (set via the Accept/Reject buttons), while `lines` is an
 * array of independently editable cost-entry rows for that supplier (one
 * supplier can quote several lines — e.g. different quantity breaks, or, for
 * a supplier holding more than one role on the Quote, different roles —
 * see `role` below).
 */

// Which cost-entry fields are relevant for each sourcing role — the same
// three roles the Selection_Wizard produces (inquiry-module-redesign) — and
// which of those are required before a line counts as "complete" for that
// role. A cost-entry line carries a `role` tag identifying which of the
// supplier's roles on this Quote it prices; a supplier holding two roles
// (e.g. Manufacturing + Services) has independent lines — and independent
// completeness — per role, since the two are genuinely different costs.
export const SUPPLIER_TYPE_FIELDS = {
  // Manufacturing excludes Shipping Cost/Time — shipping is priced on the
  // Logistics role's own line — so its lead time is Production Time alone.
  Manufacturing: ['productDetails', 'unitPrice', 'productionTime', 'validUntil'],
  Services: ['brandingDecoration', 'unitPrice', 'productionTime', 'validUntil'],
  Logistics: ['shippingCost', 'shippingTime', 'validUntil'],
};

const SUPPLIER_TYPE_REQUIRED_FIELDS = {
  // Manufacturing no longer collects Shipping Time/Cost — that's the
  // Logistics role's concern — so Production Time alone is its lead time.
  Manufacturing: ['unitPrice', 'productionTime'],
  Services: ['unitPrice', 'productionTime'],
  Logistics: ['shippingCost', 'shippingTime'],
};

export const getSupplierTypeFields = (role) => (
  SUPPLIER_TYPE_FIELDS[role] || SUPPLIER_TYPE_FIELDS.Manufacturing
);

export const getSupplierTypeRequiredFields = (role) => (
  SUPPLIER_TYPE_REQUIRED_FIELDS[role] || SUPPLIER_TYPE_REQUIRED_FIELDS.Manufacturing
);

const isPopulated = (value) => value !== null && value !== undefined && String(value).trim() !== '';

/** True iff a single cost-entry line has every field required for its role populated. */
const hasAllRequiredFields = (line = {}, role) => (
  getSupplierTypeRequiredFields(role).every((field) => isPopulated(line[field]))
);

export const isSupplierQuoteComplete = hasAllRequiredFields;

const resolveSupplierName = (quote, supplierId) => (
  (quote.suppliers || []).find((s) => String(s.supplierId) === String(supplierId))?.supplierName || ''
);

/** Resolves a supplier's legacy manual type from the `suppliers` directory, falling back to 'Product'. */
export const resolveSupplierType = (supplierId, suppliers = []) => (
  suppliers.find((s) => String(s.id) === String(supplierId))?.supplierType || 'Product'
);

// Legacy Supplier.supplierType values (pre-derivedSupplierTypes), bucketed
// into the three roles above — used only as a fallback for Quotes created
// before the Selection_Wizard existed, whose `quote.suppliers[i]` entries
// don't carry a `roles` array.
const LEGACY_SUPPLIER_TYPE_TO_ROLE = {
  Manufacturing: 'Manufacturing',
  Product: 'Manufacturing',
  Branding: 'Services',
  Customization: 'Services',
  Logistics: 'Logistics',
  Fulfilment: 'Logistics',
};

/**
 * The role(s) a supplier fulfils on a specific Quote: the Selection_Wizard's
 * own `quote.suppliers[i].roles` when present (the authoritative source —
 * a supplier can hold Manufacturing, Services, and/or Logistics at once),
 * otherwise a single-role fallback bucketed from the Supplier's legacy
 * manual `supplierType` field (pre-wizard Quotes only). Always non-empty.
 */
export const getSupplierRolesForQuote = (quote = {}, supplierId, suppliers = []) => {
  const supplierEntry = (quote.suppliers || []).find((s) => String(s.supplierId) === String(supplierId));
  if (supplierEntry?.roles?.length) return supplierEntry.roles;
  const legacyType = resolveSupplierType(supplierId, suppliers);
  return [LEGACY_SUPPLIER_TYPE_TO_ROLE[legacyType] || 'Manufacturing'];
};

/** True iff a line satisfies the required fields for at least one of the given roles (permissive — used for single-line "best guess" picks). */
const isLineCompleteForAnyRole = (line, roles) => roles.some((role) => hasAllRequiredFields(line, role));

/**
 * True iff every one of the supplier's roles has at least one line whose
 * OWN role tag matches AND is complete for that role (strict — the real
 * readiness gate). The role-match check matters: a Manufacturing line's
 * populated fields (unitPrice, productionTime, …) can incidentally satisfy
 * Services' required-field list too (a subset), so completeness must be
 * judged against the line's actual role, not just field presence.
 */
const isSupplierEntryComplete = (lines = [], roles = []) => (
  roles.length > 0 && roles.every((role) => lines.some((line) => {
    const matchesRole = line.role ? line.role === role : roles.length === 1;
    return matchesRole && hasAllRequiredFields(line, role);
  }))
);

/**
 * True iff at least one supplier on the Quote has every one of its roles
 * priced (Manufacturing/Services/Logistics each independently complete,
 * not just one of them).
 */
export const isReadyForOffer = (quote = {}, suppliers = []) => (
  Object.entries(quote.supplierQuotes || {}).some(([supplierId, entry]) => (
    isSupplierEntryComplete(entry?.lines || [], getSupplierRolesForQuote(quote, supplierId, suppliers))
  ))
);

/** Immutable merge of `patch` (e.g. `{ status }` or `{ lines }`) into `quote.supplierQuotes[supplierId]`. */
export const saveSupplierQuote = (quote, supplierId, patch = {}) => ({
  ...quote,
  supplierQuotes: {
    ...quote.supplierQuotes,
    [supplierId]: {
      status: null,
      lines: [],
      ...(quote.supplierQuotes?.[supplierId] || {}),
      ...patch,
    },
  },
});

/**
 * One entry per supplier on a Quote that has at least one line complete for
 * any one of its roles (using that line as the representative price) — used
 * for the "pick one supplier" disambiguation UI when connecting a Quote to
 * an Offer and no supplier has been Accepted yet. This is a best-guess
 * single-price pick, not the readiness gate — a multi-role supplier's full
 * completeness is `isSupplierEntryComplete`/`isReadyForOffer`.
 * (For pulling every accepted line into an Offer, see
 * `getAcceptedSupplierQuoteLines`, which is per-line, not per-supplier.)
 */
export const getCompleteSupplierQuoteOptions = (quote = {}, suppliers = []) => (
  Object.entries(quote.supplierQuotes || {}).flatMap(([supplierId, entry]) => {
    const roles = getSupplierRolesForQuote(quote, supplierId, suppliers);
    const firstComplete = (entry?.lines || []).find((line) => isLineCompleteForAnyRole(line, roles));
    if (!firstComplete) return [];
    return [{
      supplierId,
      supplierName: resolveSupplierName(quote, supplierId) || firstComplete.supplierName || supplierId,
      data: firstComplete,
    }];
  })
);

/**
 * Returns a single best-guess cost-entry line for `overrideSupplierId` if
 * given (first complete line for that supplier, else its first line),
 * otherwise the first complete line found across all suppliers, otherwise
 * null. Used as the fallback pick when no supplier has been explicitly
 * Accepted yet.
 */
export const getSelectedSupplierQuote = (quote = {}, overrideSupplierId = null, suppliers = []) => {
  const supplierQuotes = quote.supplierQuotes || {};
  if (overrideSupplierId != null && supplierQuotes[overrideSupplierId]) {
    const roles = getSupplierRolesForQuote(quote, overrideSupplierId, suppliers);
    const lines = supplierQuotes[overrideSupplierId].lines || [];
    return lines.find((line) => isLineCompleteForAnyRole(line, roles)) || lines[0] || null;
  }
  for (const [supplierId, entry] of Object.entries(supplierQuotes)) {
    const roles = getSupplierRolesForQuote(quote, supplierId, suppliers);
    const complete = (entry?.lines || []).find((line) => isLineCompleteForAnyRole(line, roles));
    if (complete) return { ...complete, supplierId };
  }
  return null;
};

/**
 * Every complete cost-entry line belonging to a supplier explicitly marked
 * Accepted (via the Quote Detail Accept/Reject buttons), each annotated
 * with its supplierId and the role it prices — one supplier can contribute
 * several lines (different quantity breaks, and/or one line per role it
 * holds on this Quote), and each becomes its own entry here.
 */
export const getAcceptedSupplierQuoteLines = (quote = {}, suppliers = []) => (
  Object.entries(quote.supplierQuotes || {}).flatMap(([supplierId, entry]) => {
    if (entry?.status !== 'accepted') return [];
    const roles = getSupplierRolesForQuote(quote, supplierId, suppliers);
    return (entry.lines || [])
      .map((line) => {
        const role = line.role && roles.includes(line.role) ? line.role : roles.find((r) => hasAllRequiredFields(line, r));
        return { line, role };
      })
      .filter(({ line, role }) => role && hasAllRequiredFields(line, role))
      .map(({ line, role }) => ({ ...line, supplierId, role }));
  })
);

/**
 * Maps Supplier_Quote cost-entry lines from each Quote into one or more
 * Offer_Item records. `supplierOverrides` is an optional `{ [quoteId]:
 * supplierId }` map letting the caller pick one specific supplier for a
 * Quote (e.g. the "Connect to Offer" disambiguation picker), which takes
 * priority over everything else for that Quote.
 *
 * Otherwise, every complete line belonging to an Accepted supplier becomes
 * its own Offer_Item — so the same product can appear as separate line
 * items (different suppliers, different roles from the same supplier,
 * and/or different lines from the same supplier/role) instead of collapsing
 * down to a single pick. A role beyond Manufacturing is called out in the
 * item's name (e.g. "Bags — Services") so a Manufacturing + Services
 * supplier's two prices read as distinct line items, not a duplicate.
 * Quotes with no explicit Accept fall back to the automatic "first
 * complete line" pick, same as before.
 */
export const buildOfferItemsFromQuotes = (quotes = [], supplierOverrides = {}, suppliers = []) => quotes.flatMap((quote) => {
  const override = supplierOverrides[quote.id];
  const accepted = getAcceptedSupplierQuoteLines(quote, suppliers);
  const selections = override != null
    ? [{ ...(getSelectedSupplierQuote(quote, override, suppliers) || {}), supplierId: override }]
    : (accepted.length > 0 ? accepted : [getSelectedSupplierQuote(quote, null, suppliers) || {}]);

  return selections.map((selected, index) => {
    const roleSuffix = selected.role && selected.role !== 'Manufacturing' ? ` — ${selected.role}` : '';
    return {
      id: selections.length > 1 ? `${quote.id}-offer-item-${selected.supplierId}-${selected.id || index}` : `${quote.id}-offer-item`,
      lineType: quote.lineType || 'Product',
      role: selected.role || null,
      serviceId: quote.serviceId ?? null,
      logisticsId: quote.logisticsId ?? null,
      linkedToLineId: quote.linkedToLineId ?? null,
      productName: `${quote.productName || ''}${roleSuffix}`,
      quantity: selected.quantity ?? quote.quantity,
      qty: selected.quantity ?? quote.quantity,
      supplierId: selected.supplierId ?? null,
      supplierName: resolveSupplierName(quote, selected.supplierId) || selected.supplierName || '',
      unitPrice: Number(selected.unitPrice || 0),
      shippingCost: Number(selected.shippingCost || 0),
      marginPercent: 0,
      discountPercent: 0,
      deliveryDate: null,
      sourceQuoteId: quote.id,
    };
  });
});

/** Sets status 'Sent to Sales Quote' and stores the Offer reference on each Quote. */
export const connectQuotesToOffer = (quotes = [], offerId) => quotes.map((quote) => ({
  ...quote,
  status: 'Sent to Sales Quote',
  convertedOfferId: offerId,
}));

/**
 * customerTotal = (unitPrice * qty + shippingCost) * (1 + margin/100) * (1 - discount/100)
 */
export const computeOfferItemInternalCost = (item = {}) => (
  Number(item.unitPrice ?? item.confirmedUnitPrice ?? 0) * Number(item.quantity ?? item.qty ?? 0)
  + Number(item.shippingCost || 0)
);

export const computeOfferItemTotal = (item = {}) => (
  computeOfferItemInternalCost(item)
  * (1 + Number(item.marginPercent || 0) / 100)
  * (1 - Number(item.discountPercent || 0) / 100)
);

const EDITABLE_OFFER_STATUSES = ['New Sales Quote', 'Sent to the Customer'];

export const canEditOfferItem = (offer = {}) => EDITABLE_OFFER_STATUSES.includes(offer.status);

/** Total Lead Time = production time + shipping time (days). */
export const computeTotalLeadTime = (supplierQuoteData = {}) => (
  Number(supplierQuoteData.productionTime || 0) + Number(supplierQuoteData.shippingTime || 0)
);

/** Total for a single cost-entry line: unit price × quantity + shipment cost. */
export const computeSupplierQuoteTotal = (supplierQuoteData = {}, quantity = 0) => (
  (Number(supplierQuoteData.unitPrice || 0) * Number(quantity || 0))
  + Number(supplierQuoteData.shippingCost || 0)
);

/**
 * Migrates a Quote's `supplierQuotes` map from the old shape (one flat
 * cost-entry object per supplier) to the current `{ status, lines }` shape.
 * Already-migrated entries (with a `lines` array) pass through unchanged.
 */
export const migrateLegacySupplierQuotes = (quote) => {
  const supplierQuotes = quote?.supplierQuotes || {};
  const migrated = {};
  let changed = false;
  Object.entries(supplierQuotes).forEach(([supplierId, entry]) => {
    if (entry && Array.isArray(entry.lines)) {
      migrated[supplierId] = entry;
      return;
    }
    changed = true;
    const { status, ...lineFields } = entry || {};
    const hasAnyData = Object.entries(lineFields).some(([key, value]) => (
      key !== 'supplierId' && isPopulated(value)
    ));
    migrated[supplierId] = {
      status: status || null,
      lines: hasAnyData ? [{ id: `${supplierId}-line-1`, quantity: quote?.quantity ?? null, ...lineFields }] : [],
    };
  });
  return changed ? { ...quote, supplierQuotes: migrated } : quote;
};
